import type {FacetModel} from "../../components/filter/model";
import {
	FETCH_MAINTENANCE_CATEGORY_ILLUSTRATIONS_IN_PROPOSAL,
	FETCH_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL,
	FETCH_MAINTENANCE_SECTIONS_FACETS_IN_PROPOSAL,
	FETCH_MAINTENANCE_TASK_SPARES_IN_PROPOSAL,
	FETCH_SPARE_PART_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL,
	RESET_MAINTENANCE_GUIDE_TIME_SEARCH_IN_PROPOSAL,
	RESET_MAINTENANCE_TASK_SPARES_IN_PROPOSAL,
	RESET_MATCHING_GUIDE_TIMES_IN_PROPOSAL,
	RESET_SELECTED_MAINTENANCE_CATEGORY_IN_PROPOSAL,
	SET_MAINTENANCE_GUIDE_TIME_FILTER_IN_PROPOSAL,
	SET_MAINTENANCE_GUIDE_TIME_LAST_VEHICLE_CONTEXT_IN_PROPOSAL,
	SET_MAINTENANCE_TASK_SEARCH_CONTEXT,
	SET_SELECTED_MAINTENANCE_CATEGORY_IN_PROPOSAL,
	SET_SPARE_PART_MAINTENANCE_GUIDE_TIME_IN_PROPOSAL
} from "../actions/proposalMaintenanceGuideTimes";
import type {MaintenanceGuideTimeModel, MaintenanceIllustration} from "../model";
import {
	MaintenanceGuideTimeSearchVehicleParams,
	MaintenanceTaskPosition, MaintenanceTaskSearchContext,
	MatchingMaintenanceGuideTimesResponse
} from "../model";


export type ProposalMaintenanceGuideTimeSearchState = {
	lastVehicleContext: MaintenanceGuideTimeSearchVehicleParams,
	isFetchingMaintenanceSectionFacets: boolean,
	isFetchingMatchingMaintenanceGuideTimeModels: boolean,
	isFetchingMaintenanceIllustrations: boolean,
	selectedMaintenanceCategoryId: number,
	maintenanceSectionFacets: FacetModel[],
	matchingMaintenanceGuideTimesResponse: MatchingMaintenanceGuideTimesResponse,
	sparePartGuideTime: {},
	selectedSparePartGuideTimeModels: MaintenanceGuideTimeModel[],
	maintenanceIllustrations: MaintenanceIllustration[],
	isMaintenanceGuideTimeFilterActive: boolean,
	isFetchingMaintenanceTaskSpares: boolean,
	maintenanceTaskPositions: MaintenanceTaskPosition[],
	maintenanceTaskSearchContext: $Values<typeof MaintenanceTaskSearchContext>
}

const initialState: ProposalMaintenanceGuideTimeSearchState = {
	lastVehicleContext: {
		modelYear: "",
		type: ""
	},
	isFetchingMaintenanceSectionFacets: false,
	isFetchingMatchingMaintenanceGuideTimeModels: false,
	isFetchingMaintenanceIllustrations: false,
	selectedMaintenanceCategoryId: null,
	maintenanceSectionFacets: [],
	matchingMaintenanceGuideTimesResponse: null,
	sparePartGuideTime: {},
	selectedSparePartGuideTimeModels: [],
	maintenanceIllustrations: [],
	isMaintenanceGuideTimeFilterActive: false,
	isFetchingMaintenanceTaskSpares: false,
	maintenanceTaskPositions: [],
	maintenanceTaskSearchContext: MaintenanceTaskSearchContext.TASK_CATALOG
};

const proposalGuideTimeSearchReducer = (state: ProposalMaintenanceGuideTimeSearchState = initialState, action): ProposalMaintenanceGuideTimeSearchState => {
	switch (action.type) {
		case SET_SELECTED_MAINTENANCE_CATEGORY_IN_PROPOSAL:
			return {
				...state,
				selectedMaintenanceCategoryId: action.payload
			};
		case RESET_SELECTED_MAINTENANCE_CATEGORY_IN_PROPOSAL:
			return {
				...state,
				selectedMaintenanceCategoryId: null
			};
		case RESET_MAINTENANCE_GUIDE_TIME_SEARCH_IN_PROPOSAL:
			return {
				...state,
				selectedMaintenanceCategoryId: null,
				maintenanceIllustrations: [],
				matchingMaintenanceGuideTimesResponse: null
			};
		case SET_MAINTENANCE_GUIDE_TIME_FILTER_IN_PROPOSAL:
			return {
				...state,
				isMaintenanceGuideTimeFilterActive: action.payload
			};
		case SET_MAINTENANCE_GUIDE_TIME_LAST_VEHICLE_CONTEXT_IN_PROPOSAL:
			return {
				...state,
				lastVehicleContext: {
					modelYear: action.payload.modelYear,
					type: action.payload.type
				},
			};
		case FETCH_MAINTENANCE_SECTIONS_FACETS_IN_PROPOSAL.REQUEST:
			return {
				...state,
				isFetchingMaintenanceSectionsFacets: true
			};
		case FETCH_MAINTENANCE_SECTIONS_FACETS_IN_PROPOSAL.SUCCESS:
			return {
				...state,
				maintenanceSectionFacets: action.payload,
				isFetchingMaintenanceSectionsFacets: false
			};
		case FETCH_MAINTENANCE_SECTIONS_FACETS_IN_PROPOSAL.FAILURE:
			return {
				...state,
				isFetchingMaintenanceSectionsFacets: false
			};
		case FETCH_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL.REQUEST:
			return {
				...state,
				isFetchingMatchingMaintenanceGuideTimeModels: true
			};
		case FETCH_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL.SUCCESS:
			return {
				...state,
				matchingMaintenanceGuideTimesResponse: action.payload,
				isFetchingMatchingMaintenanceGuideTimeModels: false
			};
		case FETCH_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL.FAILURE:
			return {
				...state,
				isFetchingMatchingMaintenanceGuideTimeModels: false
			};
		case FETCH_SPARE_PART_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL.REQUEST:
			return {
				...state,
				isFetchingMatchingMaintenanceGuideTimeModels: true
			};
		case FETCH_SPARE_PART_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL.SUCCESS:
			return {
				...state,
				isFetchingMatchingMaintenanceGuideTimeModels: false,
				sparePartGuideTime: {
					...state.sparePartGuideTime,
					...{
						[action.payload.productNumber]: action.payload.sparePartMaintenanceGuideTimes
					}
				},
			};
		case FETCH_SPARE_PART_MAINTENANCE_GUIDE_TIME_MODELS_IN_PROPOSAL.FAILURE:
			return {
				...state,
				isFetchingMatchingMaintenanceGuideTimeModels: false
			};
		case FETCH_MAINTENANCE_CATEGORY_ILLUSTRATIONS_IN_PROPOSAL.REQUEST:
			return {
				...state,
				isFetchingMaintenanceIllustrations: true
			};
		case FETCH_MAINTENANCE_CATEGORY_ILLUSTRATIONS_IN_PROPOSAL.SUCCESS:
			return {
				...state,
				maintenanceIllustrations: action.payload,
				isFetchingMaintenanceIllustrations: false
			};
		case FETCH_MAINTENANCE_CATEGORY_ILLUSTRATIONS_IN_PROPOSAL.FAILURE:
			return {
				...state,
				isFetchingMaintenanceIllustrations: false
			};
		case FETCH_MAINTENANCE_TASK_SPARES_IN_PROPOSAL.REQUEST:
			return {
				...state,
				isFetchingMaintenanceTaskSpares: true
			};
		case FETCH_MAINTENANCE_TASK_SPARES_IN_PROPOSAL.SUCCESS:
			return {
				...state,
				maintenanceTaskPositions: action.payload,
				isFetchingMaintenanceTaskSpares: false
			};
		case FETCH_MAINTENANCE_TASK_SPARES_IN_PROPOSAL.FAILURE:
			return {
				...state,
				isFetchingMaintenanceTaskSpares: false
			};
		case RESET_MAINTENANCE_TASK_SPARES_IN_PROPOSAL:
			return {
				...state,
				maintenanceTaskPositions: []
			};
		case RESET_MATCHING_GUIDE_TIMES_IN_PROPOSAL:
			return {
				...state,
				matchingMaintenanceGuideTimesResponse: null
			};
		case SET_SPARE_PART_MAINTENANCE_GUIDE_TIME_IN_PROPOSAL:
			return {
				...state,
				selectedSparePartGuideTimeModels: state.sparePartGuideTime[action.payload].maintenanceGuideTimes,
			};
		case SET_MAINTENANCE_TASK_SEARCH_CONTEXT:
			return {
				...state,
				maintenanceTaskSearchContext: action.payload,
			};
		default:
			return state;
	}
};

export default proposalGuideTimeSearchReducer;